import React, { useMemo, useState } from "react";
import { IconButton, Menu, MenuItem, ListItemText, ListItemIcon, Tooltip, Divider } from "@mui/material";
import { HomeRounded, HistoryRounded, StarRounded, StarBorderRounded, CloseRounded } from "@mui/icons-material";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppContext } from "context";
import { buildMenu } from "components/body/menuConfig";
import { getRecent, getFavorites, toggleFavorite } from "utils/navPrefs";

/**
 * Tier-2 left cluster on the dark nav bar: Home, Recent, Favorites.
 * Recent/Favorites are localStorage-backed (utils/navPrefs).
 */
const NavShortcuts = () => {
  const { access, settings } = useAppContext();
  // Default ns = "nav" so buildMenu() resolves menu titles; common keys are prefixed.
  const { t } = useTranslation(["nav", "common"]);
  const location = useLocation();

  const [recentAnchor, setRecentAnchor] = useState(null);
  const [favAnchor, setFavAnchor] = useState(null);
  const [favVersion, setFavVersion] = useState(0);

  // Flat menu (for resolving the current page's title when starring it).
  const flat = useMemo(() => {
    const out = [];
    buildMenu(access, settings, t).forEach((g) => {
      if (g.children?.length) g.children.forEach((c) => out.push({ title: c.title, path: c.path }));
      else if (g.path) out.push({ title: g.title, path: g.path });
    });
    return out;
  }, [access, settings, t]);

  const recent = getRecent();
  const favorites = getFavorites();
  const favPaths = new Set(favorites.map((f) => f.path));

  const current = flat.find((f) => location.pathname.includes(f.path));
  const currentIsFav = current && favPaths.has(current.path);

  const onToggleFav = (e, item) => {
    // Inside a <Link> row: stop the click from reaching the anchor (no navigate,
    // no new-tab) — this control only toggles the favorite.
    e.preventDefault();
    e.stopPropagation();
    toggleFavorite(item);
    setFavVersion((v) => v + 1);
  };

  const iconSx = {
    color: "var(--white-color)",
    opacity: 0.9,
    padding: "5px",
    "& svg": { fontSize: 20 },
    "&:hover": { opacity: 1 },
  };

  return (
    <div className="flex items-center gap-0.5 pe-2 me-1 border-e border-white/20">
      <Tooltip title={t("nav:home", "Home")}>
        <IconButton component={Link} to="/" size="small" sx={iconSx}>
          <HomeRounded fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title={t("common:recent", "Recent")}>
        <IconButton size="small" sx={iconSx} onClick={(e) => setRecentAnchor(e.currentTarget)}>
          <HistoryRounded fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title={t("common:favorites", "Favorites")}>
        <IconButton size="small" sx={iconSx} onClick={(e) => setFavAnchor(e.currentTarget)}>
          <StarRounded fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Recent menu */}
      <Menu anchorEl={recentAnchor} open={Boolean(recentAnchor)} onClose={() => setRecentAnchor(null)}>
        {recent.length === 0 ? (
          <MenuItem disabled>{t("common:noRecent", "No recent pages")}</MenuItem>
        ) : (
          recent.map((r) => (
            <MenuItem
              key={r.path}
              component={Link}
              to={r.path}
              onClick={() => setRecentAnchor(null)}
              sx={{ minWidth: 240 }}
            >
              <ListItemIcon><HistoryRounded fontSize="small" /></ListItemIcon>
              <ListItemText primary={r.title} />
            </MenuItem>
          ))
        )}
      </Menu>

      {/* Favorites menu */}
      <Menu anchorEl={favAnchor} open={Boolean(favAnchor)} onClose={() => setFavAnchor(null)} key={favVersion}>
        {current && (
          <MenuItem onClick={(e) => onToggleFav(e, current)} sx={{ minWidth: 260 }}>
            <ListItemIcon>
              {currentIsFav ? <StarRounded fontSize="small" className="text-amber-500" /> : <StarBorderRounded fontSize="small" />}
            </ListItemIcon>
            <ListItemText primary={currentIsFav ? t("common:removeCurrentPage", "Remove current page") : t("common:addCurrentPage", "Add current page")} />
          </MenuItem>
        )}
        {current && favorites.length > 0 && <Divider />}
        {favorites.length === 0 ? (
          <MenuItem disabled>{t("common:noFavorites", "No favorites yet")}</MenuItem>
        ) : (
          favorites.map((f) => (
            <MenuItem
              key={f.path}
              component={Link}
              to={f.path}
              onClick={() => setFavAnchor(null)}
              sx={{ minWidth: 260 }}
            >
              <ListItemIcon><StarRounded fontSize="small" className="text-amber-500" /></ListItemIcon>
              <ListItemText primary={f.title} />
              <IconButton component="span" size="small" onClick={(e) => onToggleFav(e, f)} sx={{ ml: 1 }}>
                <CloseRounded sx={{ fontSize: 14 }} />
              </IconButton>
            </MenuItem>
          ))
        )}
      </Menu>
    </div>
  );
};

export default NavShortcuts;
